import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

import { ImpersonationModal } from '../components/ImpersonationModal';

interface ModalState {
  isOpen: boolean;
  linkUrl: string;
  onCloseCallback?: () => void;
}

interface ImpersonationModalContextType {
  openModal: (linkUrl: string, onCloseCallback?: () => void) => void;
}

interface ImpersonationModalProviderProps {
  children: ReactNode;
}

const ImpersonationModalContext = createContext<ImpersonationModalContextType | undefined>(undefined);

export const ImpersonationModalProvider: React.FC<ImpersonationModalProviderProps> = ({ children }) => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    linkUrl: '',
  });

  const openModal = useCallback(
    (linkUrl: string, onCloseCallback?: () => void) => {
      if (modalState.isOpen) {
        throw new Error('Modal is already open. Close the current modal before opening a new one.');
      }

      setModalState({
        isOpen: true,
        linkUrl,
        onCloseCallback,
      });
    },
    [modalState.isOpen]
  );

  const closeModal = useCallback(() => {
    if (modalState.onCloseCallback) {
      modalState.onCloseCallback();
    }

    setModalState(prevState => ({
      ...prevState,
      isOpen: false,
      onCloseCallback: undefined,
    }));
  }, [modalState]);

  const contextValue = useMemo(
    () => ({
      openModal,
    }),
    [openModal]
  );

  return (
    <ImpersonationModalContext.Provider value={contextValue}>
      {children}
      <ImpersonationModal closeModal={closeModal} linkUrl={modalState.linkUrl} isOpen={modalState.isOpen} />
    </ImpersonationModalContext.Provider>
  );
};

export const useImpersonationModal = () => {
  const context = useContext(ImpersonationModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within an ImpersonationModalProvider');
  }
  return context;
};
